import { usePageTitle } from '../hooks/usePageTitle'
import {
  BrassRule,
  ClubLinkButton,
  ClubPage,
  Container,
  Crest,
  Eyebrow,
  SectionHead,
} from '../components/golf'
import {
  foundingPlate,
  membershipTiers,
  PRIVILEGES,
  tierNote,
  type FoundingPlate,
  type MembershipTier,
  type PrivilegeValue,
} from '../lib/membership'

/**
 * What the founding section says about the plate, from the plate itself.
 *
 * Null when the shell carried no plate at all — the line is omitted rather
 * than falling back to a count nobody sent.
 *
 * ⚠️ It names no NUMBER. "No. 001 is still to be had" stood here and is false
 * the moment any member of any tier exists: the club strikes numbers as
 * `max + 1` ACROSS EVERY TIER, so three weekenders on the books make the next
 * founding seat No. 004. An empty plate is a fact the payload carries; which
 * number the next founder gets is not, until it is struck.
 */
function foundingPlateLine({ taken, seats }: FoundingPlate): string | null {
  if (seats === 0) return null
  if (taken === 0) return `The plate is open · not one of the ${seats} is struck`
  if (taken >= seats) return `The plate is closed · all ${seats} are struck`
  return `${taken} of ${seats} spoken for · the plate is still open`
}

/**
 * The Terms of Membership (route map §4 `/membership`) — the privileges
 * matrix, the founding hundred, the procedure, and the fine print.
 *
 * The design card's "Thirteen seats remain · No. 088 is next" is replaced: no
 * seats have been taken, and a countdown that invents scarcity is the one bit
 * of this page that would actually mislead someone into paying. The founding
 * offer itself — one hundred, rate for life — is stated as written.
 */

const PROCEDURE = [
  {
    numeral: 'I.',
    title: 'Apply',
    body: 'Give your name and an address for the post. We send a sign-in link — no password to mind, no committee to charm.',
  },
  {
    numeral: 'II.',
    title: 'Settle your dues',
    body: 'Stripe keeps the ledger. The moment your dues clear, your number is struck and your card is raised.',
  },
  {
    numeral: 'III.',
    title: 'Take your first round',
    body: 'Walk straight onto the grounds. Nine holes or one — the course will not check its watch.',
  },
]

const FAQ = [
  {
    q: 'Is the golf real?',
    a: 'The club is real; the grass is rendered. Your standing, your scores, and your dues are entirely genuine — only the divots are simulated, and nobody has ever missed them.',
  },
  {
    q: 'What do the dues cover?',
    a: 'The full keeping of the club: the grounds, the register, the lounge, your card and number, and a handicap maintained with rather more ceremony than it strictly deserves.',
  },
  {
    q: 'May I change my standing?',
    a: 'At any hour, from your account. Weekender to Full, Full to Founding while seats remain — the difference is prorated without fuss or paperwork.',
  },
  {
    q: 'And if I must resign?',
    a: 'Two clicks, effective at once — no letter required, no exit interview granted. Proximity to the exit, always.',
  },
  {
    q: 'What makes a founding seat worth keeping?',
    a: 'The seal on your card, your name in the charter, and the rate held for life. One hundred exist. They are not reissued.',
  },
  {
    q: 'Is there a dress code?',
    a: 'There is the spirit of one. Wear whatever survives your calendar; the club will assume the blazer.',
  },
]

/**
 * A single matrix cell. The brass ring is decorative, so the state is always
 * spelled out for assistive tech — otherwise every row reads as an identical
 * run of blanks.
 */
function PrivilegeCell({ value }: { value: PrivilegeValue }) {
  if (typeof value === 'string') {
    return <span className="text-[13px] text-ink">{value}</span>
  }

  return (
    <>
      <span
        aria-hidden="true"
        className={`inline-block size-2.5 rounded-full border-[1.5px] border-brass align-middle ${
          value ? 'bg-brass' : ''
        }`}
      />
      <span className="sr-only">{value ? 'Among the privileges' : 'Not at this standing'}</span>
    </>
  )
}

const CTA_LABELS: Record<MembershipTier['id'], string> = {
  weekender: 'Apply',
  full: 'Apply for membership',
  founding: 'Claim a founding seat',
}

export function MembershipScreen() {
  usePageTitle('Membership')
  const plateLine = foundingPlateLine(foundingPlate())

  return (
    <ClubPage>
      {/* ── Masthead ── */}
      <section className="pb-16 pt-20 text-center">
        <Container>
          <Eyebrow>Membership · Chartered MMXXVI</Eyebrow>
          <h1 className="mx-auto mt-5 max-w-[20ch] font-display text-[clamp(2.625rem,5.6vw,4.5rem)] font-medium leading-[1.04] tracking-[-0.018em] text-pine">
            The Terms of <em className="italic text-fairway">Membership.</em>
          </h1>
          <p className="mx-auto mt-5.5 max-w-[52ch] text-[17px] text-ink-soft">
            Three standings, one club. Read the privileges the way you&rsquo;d read a scorecard —
            plainly, and ruled in brass.
          </p>
          <BrassRule className="mx-auto mt-7.5 w-21" />
        </Container>
      </section>

      {/* ── The privileges matrix ── */}
      <section className="pb-22">
        <Container>
          <div className="rounded-club-lg border border-ink/12 bg-parchment px-4 pb-7 pt-3.5 shadow-[0_30px_60px_-44px_rgba(21,58,42,0.5)] sm:px-8.5">
            {/* The matrix cannot compress below four readable columns, so it
                scrolls in its own well rather than shrinking the page. The well
                is `relative` because the cells' `sr-only` text is absolutely
                positioned: without a positioned scroll container those boxes
                escape the well into the page, and at 390px the page itself
                panned 297px to reach them. */}
            <div className="relative overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse tabular-nums">
                <caption className="sr-only">
                  Privileges included at each membership standing
                </caption>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="border-b border-brass px-3.5 pb-2.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep"
                    >
                      The Privileges
                    </th>
                    {membershipTiers().map((tier) => (
                      <th
                        key={tier.id}
                        scope="col"
                        className={`w-[22%] border-b border-brass px-3.5 pb-4.5 pt-6.5 text-center align-bottom ${
                          tier.id === 'full' ? 'bg-sage-soft/38' : ''
                        }`}
                      >
                        {tier.id === 'full' && (
                          <span className="mb-2.5 inline-block rounded-club-sm bg-brass-deep px-2.5 py-1 text-[9.5px] font-bold uppercase tracking-[0.16em] text-parchment">
                            Most Chosen
                          </span>
                        )}
                        <span className="block font-display text-[21px] font-medium normal-case tracking-normal text-pine">
                          {tier.name}
                        </span>
                        <span className="mt-0.5 block text-[11.5px] font-medium normal-case tracking-normal text-ink-soft">
                          {tierNote(tier.id)}
                        </span>
                        <span className="mt-3 block font-display text-[31px] font-medium tracking-normal text-pine">
                          {tier.price}
                          <span className="align-baseline font-text text-xs font-medium normal-case tracking-normal text-ink-soft">
                            /{tier.interval}
                          </span>
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PRIVILEGES.map((privilege) => (
                    <tr key={privilege.label}>
                      <th
                        scope="row"
                        className="border-b border-brass/25 px-3.5 py-3.5 text-left text-sm font-normal text-ink"
                      >
                        {privilege.label}
                      </th>
                      <td className="border-b border-brass/25 px-3.5 py-3.5 text-center">
                        <PrivilegeCell value={privilege.weekender} />
                      </td>
                      <td className="border-b border-brass/25 bg-sage-soft/38 px-3.5 py-3.5 text-center">
                        <PrivilegeCell value={privilege.full} />
                      </td>
                      <td className="border-b border-brass/25 px-3.5 py-3.5 text-center">
                        <PrivilegeCell value={privilege.founding} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="px-3.5 pb-2 pt-6" />
                    {membershipTiers().map((tier) => (
                      <td
                        key={tier.id}
                        className={`px-3.5 pb-2 pt-6 text-center ${tier.id === 'full' ? 'bg-sage-soft/38' : ''}`}
                      >
                        {tier.soldOut ? (
                          // A finite tier that has run out. Saying so is the
                          // whole point — a button here would be refused at
                          // checkout, which is a control that lies.
                          <span className="block w-full rounded-club-sm border border-dashed border-brass/45 px-3.5 py-3 text-[13px] text-ink-soft">
                            All one hundred struck
                          </span>
                        ) : (
                          <ClubLinkButton
                            to="/join"
                            search={{ tier: tier.id }}
                            variant={tier.id === 'full' ? 'ink' : 'ghost'}
                            className="w-full !px-3.5 !py-3 !text-[13px]"
                          >
                            {CTA_LABELS[tier.id]}
                          </ClubLinkButton>
                        )}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-7.5 gap-y-2 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-ink-soft">
              <span className="flex items-center gap-2.5">
                <span aria-hidden="true" className="inline-block size-2.5 rounded-full border-[1.5px] border-brass bg-brass" />
                Among the privileges
              </span>
              <span className="flex items-center gap-2.5">
                <span aria-hidden="true" className="inline-block size-2.5 rounded-full border-[1.5px] border-brass" />
                Not at this standing
              </span>
              <span className="text-xs font-medium normal-case tracking-[0.04em]">
                Dues in U.S. dollars · Change or resign your standing at any time
              </span>
            </div>
          </div>
        </Container>
      </section>

      {/* ── The founding hundred ── */}
      <section className="border-y border-brass/30 bg-paper-deep py-21">
        <Container className="grid items-center gap-14 lg:grid-cols-[190px_1fr]">
          <Crest monogram className="mx-auto w-full max-w-[190px]" />
          <div>
            <Eyebrow>The Founding Hundred</Eyebrow>
            <h2 className="mb-4 mt-3.5 font-display text-[clamp(1.875rem,3.8vw,2.625rem)] font-medium leading-[1.04] text-pine">
              One hundred seats. Not one more.
            </h2>
            <p className="max-w-[58ch] text-[15.5px] text-ink-soft">
              Founding members take the seal on their card, their name in the charter, and a rate
              held for life — $290 a year, for as long as you keep your standing. When the hundredth
              seat is taken, the plate is closed and the engraver goes home.
            </p>
            {/* Was "The plate is open · No. 001 is still to be had", asserted
                whatever the truth was — and the card's own "Thirteen seats
                remain · No. 088 next" before that. Read from the plate the
                shell injects, omitted entirely when it carries none, and
                naming no number in any state: numbers are struck across every
                tier, so the club cannot know the next one until it strikes it. */}
            {plateLine && (
              <p className="mt-5.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-brass-deep">
                {plateLine}
              </p>
            )}
          </div>
        </Container>
      </section>

      {/* ── How one joins ── */}
      <section className="pb-21 pt-22">
        <Container>
          <SectionHead centered eyebrow="The Procedure" title="How one joins" className="mb-13" />
          <div className="grid gap-8 sm:grid-cols-3">
            {PROCEDURE.map((step) => (
              <div key={step.title}>
                <BrassRule short className="mb-4" />
                <span className="font-display text-[15px] text-brass-deep">{step.numeral}</span>
                <h3 className="mb-2 mt-2.5 font-display text-xl font-medium text-pine">
                  {step.title}
                </h3>
                <p className="text-[14.5px] text-ink-soft">{step.body}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── The fine print ── */}
      <section className="border-t border-brass/30 bg-paper-deep pb-22 pt-21">
        <Container>
          <SectionHead centered eyebrow="The Fine Print" title="Asked in the clubhouse" className="mb-13" />
          <dl className="mx-auto max-w-[860px] border-t border-brass/35">
            {FAQ.map((item) => (
              <div
                key={item.q}
                className="grid gap-2.5 border-b border-brass/35 px-1.5 py-6.5 md:grid-cols-[250px_1fr] md:gap-8.5"
              >
                <dt className="font-display text-[19px] leading-[1.3] text-pine">{item.q}</dt>
                <dd className="max-w-[60ch] text-[14.5px] text-ink-soft">{item.a}</dd>
              </div>
            ))}
          </dl>
        </Container>
      </section>

      {/* ── Membership is open ── */}
      <section className="on-pine relative overflow-hidden bg-pine py-24 text-center text-paper">
        <span aria-hidden="true" className="absolute -left-20 -top-30 size-[340px] rounded-full border border-gilt/25" />
        <span aria-hidden="true" className="absolute -bottom-28 -right-10 size-[260px] rounded-full border border-gilt/25" />
        <Container>
          <Eyebrow tone="gilt">Membership Is Open</Eyebrow>
          <h2 className="mx-auto mb-5 mt-4 max-w-[18ch] font-display text-[clamp(2rem,4.4vw,3.25rem)] font-medium leading-[1.04] text-paper">
            Take up your standing.
          </h2>
          <p className="mx-auto mb-8 max-w-[46ch] text-base text-sage">
            A minute to apply, a moment to settle the dues — and the register carries your name by
            evening. The first hundred are inscribed for good.
          </p>
          <ClubLinkButton to="/join">Apply for membership</ClubLinkButton>
        </Container>
      </section>
    </ClubPage>
  )
}
